import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { CheckCircle, XCircle, Loader, Mail, Shield, ArrowRight } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface ConfirmationState {
  status: 'loading' | 'success' | 'error';
  message: string;
  redirectCountdown?: number;
}

const MagicLinkConfirmation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [confirmationState, setConfirmationState] = useState<ConfirmationState>({
    status: 'loading',
    message: 'Verifying your magic link...'
  });

  useEffect(() => {
    verifyMagicLink();
  }, [location]);

  const verifyMagicLink = async () => {
    try {
      console.log('Starting magic link verification');
      console.log('Full URL:', window.location.href);
      console.log('Search params:', location.search);
      console.log('Hash:', location.hash);

      // 🔥 CRITICAL PRIORITY: Check for password recovery flow FIRST before any other processing
      // This ensures old password reset emails that redirect to /magic-link-confirmation still work
      // Detection logic: Check both hash and search params for type=recovery
      const hashParams = new URLSearchParams(location.hash.substring(1));
      const searchParams = new URLSearchParams(location.search);
      
      const typeInHash = hashParams.get('type');
      const typeInSearch = searchParams.get('type');
      
      const isRecovery = typeInHash === 'recovery' || typeInSearch === 'recovery';
      
      if (isRecovery) {
        console.log('🔄 RECOVERY DETECTED: Old password reset email detected - forwarding to /partner-password-reset');
        console.log('🔗 Original URL:', window.location.href);
        console.log('📦 Preserving URL params:', `${location.search}${location.hash}`);
        console.log('🎯 Target redirect:', `/partner-password-reset${location.search}${location.hash}`);
        
        // 🎯 CRITICAL: Forward to /partner-password-reset preserving ALL tokens and params
        // This ensures backwards compatibility with old recovery emails that still redirect here
        // IMPORTANT: Do NOT parse/rebuild tokens - preserve search + hash exactly as-is
        navigate(`/partner-password-reset${location.search}${location.hash}`, { replace: true });
        return; // Exit early - let /partner-password-reset handle the actual password reset
      }

      console.log('✅ NOT a recovery flow - proceeding with normal magic link verification');

      // STEP 1: Check for PKCE flow code parameter (most common)
      const code = searchParams.get('code');
      
      if (code) {
        console.log('Found code parameter, exchanging for session');
        
        const { data, error } = await supabase.auth.exchangeCodeForSession(code);
        
        if (error) {
          console.error('Code exchange error:', error);
          setConfirmationState({
            status: 'error',
            message: error.message || 'Failed to verify your magic link. The link may have expired.'
          });
          return;
        }

        if (data.session) {
          console.log('Session obtained successfully via code exchange');
          
          // Check if user needs to set up password
          const { data: { user } } = await supabase.auth.getUser();
          
          const isNewUser = user?.created_at && 
            (new Date().getTime() - new Date(user.created_at).getTime()) < 60000;
          
          if (isNewUser) {
            console.log('New user detected - redirecting to password setup');
            setConfirmationState({
              status: 'success',
              message: 'Your account has been confirmed! Please set up your password.',
              redirectCountdown: 3
            });

            let countdown = 3;
            const countdownInterval = setInterval(() => {
              countdown--;
              if (countdown <= 0) {
                clearInterval(countdownInterval);
                navigate('/partner-password-setup');
              } else {
                setConfirmationState(prev => ({
                  ...prev,
                  redirectCountdown: countdown
                }));
              }
            }, 1000);
            return;
          }
          
          // Existing user - proceed to dashboard
          setConfirmationState({
            status: 'success',
            message: 'Your account has been confirmed successfully!',
            redirectCountdown: 3
          });

          let countdown = 3;
          const countdownInterval = setInterval(() => {
            countdown--;
            if (countdown <= 0) {
              clearInterval(countdownInterval);
              navigate('/partner-dashboard');
            } else {
              setConfirmationState(prev => ({
                ...prev,
                redirectCountdown: countdown
              }));
            }
          }, 1000);
          return;
        }
      }

      // STEP 2: Check for errors in URL
      const errorDescription = hashParams.get('error_description') || searchParams.get('error_description');
      const error = hashParams.get('error') || searchParams.get('error');

      if (error || errorDescription) {
        console.error('Magic link error in URL:', { error, errorDescription });
        setConfirmationState({
          status: 'error',
          message: errorDescription || error || 'Invalid or expired magic link. Please request a new one.'
        });
        return;
      }

      // STEP 3: Check for implicit flow (hash-based session)
      console.log('No code found, checking for implicit flow session');
      
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError) {
        console.error('Session error:', sessionError);
        setConfirmationState({
          status: 'error',
          message: 'Failed to verify your session. Please try logging in again.'
        });
        return;
      }

      if (session) {
        console.log('Session found via implicit flow');
        setConfirmationState({
          status: 'success',
          message: 'Your account has been confirmed successfully!',
          redirectCountdown: 3
        });

        let countdown = 3;
        const countdownInterval = setInterval(() => {
          countdown--;
          if (countdown <= 0) {
            clearInterval(countdownInterval);
            navigate('/partner-dashboard');
          } else {
            setConfirmationState(prev => ({
              ...prev,
              redirectCountdown: countdown
            }));
          }
        }, 1000);
        return;
      }

      // STEP 4: No session found after all attempts
      console.error('No session found after verification attempts');
      setConfirmationState({
        status: 'error',
        message: 'Invalid or missing confirmation token. Please request a new magic link.'
      });

    } catch (error: any) {
      console.error('Unexpected verification error:', error);
      setConfirmationState({
        status: 'error',
        message: `An unexpected error occurred: ${error.message || 'Please try again'}`
      });
    }
  };

  const handleRequestNewLink = () => {
    navigate('/partner-login');
  };

  const handleResendMagicLink = async () => {
    const email = location.search.split('email=')[1];
    if (!email) {
      alert('Email not found. Please contact support.');
      return;
    }

    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: email,
        options: {
          emailRedirectTo: `${window.location.origin}/magic-link-confirmation`
        }
      });

      if (error) throw error;

      alert('New magic link sent! Please check your email.');
    } catch (error: any) {
      alert(`Failed to resend: ${error.message}`);
    }
  };

  const renderContent = () => {
    switch (confirmationState.status) {
      case 'loading':
        return (
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 mb-6 rounded-full bg-blue-100">
              <Loader className="w-10 h-10 text-blue-600 animate-spin" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              Verifying Your Account
            </h2>
            <p className="text-gray-600 mb-4">
              {confirmationState.message}
            </p>
            <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
              <Shield className="w-4 h-4" />
              <span>Secure verification in progress</span>
            </div>
          </div>
        );

      case 'success':
        return (
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 mb-6 rounded-full bg-green-100">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              Account Confirmed!
            </h2>
            <p className="text-gray-600 mb-6">
              {confirmationState.message}
            </p>
            
            {confirmationState.redirectCountdown !== undefined && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-center space-x-2 text-blue-700 mb-2">
                  <ArrowRight className="w-5 h-5" />
                  <span className="font-semibold">Redirecting in {confirmationState.redirectCountdown} seconds</span>
                </div>
                <p className="text-sm text-blue-600">
                  Taking you to your partner dashboard...
                </p>
              </div>
            )}

            <button
              onClick={() => navigate('/partner-dashboard')}
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Go to Dashboard Now
              <ArrowRight className="ml-2 w-5 h-5" />
            </button>

            <div className="mt-8 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Next Steps
              </h3>
              <ul className="text-left space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Complete your partner profile</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Set up your business information</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Create your first offer</span>
                </li>
              </ul>
            </div>
          </div>
        );

      case 'error':
        return (
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 mb-6 rounded-full bg-red-100">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              Verification Failed
            </h2>
            <p className="text-gray-600 mb-6">
              {confirmationState.message}
            </p>

            <div className="space-y-3">
              <button
                onClick={handleRequestNewLink}
                className="w-full inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                <Mail className="mr-2 w-5 h-5" />
                Request New Magic Link
              </button>
              
              <button
                onClick={() => navigate('/partner-login')}
                className="w-full inline-flex items-center justify-center px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                Back to Login
              </button>
            </div>

            <div className="mt-6 text-sm text-gray-500">
              <p>Need help?</p>
              <a 
                href="mailto:support@travelheroes.at" 
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                Contact Support
              </a>
            </div>
          </div>
        );
    }
  };

  return (
    <>
      <Helmet>
        <title>Confirm Account - TravelHeroes Partner</title>
        <meta name="description" content="Confirm your TravelHeroes partner account" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              TravelHeroes
            </h1>
            <p className="text-gray-600">Partner Portal</p>
          </div>

          {/* Main Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            {renderContent()}
          </div>

          {/* Security Notice */}
          <div className="mt-6 text-center text-xs text-gray-500">
            <div className="flex items-center justify-center space-x-1 mb-1">
              <Shield className="w-3 h-3" />
              <span>Secured by Supabase Authentication</span>
            </div>
            <p>© 2026 TravelHeroes. All rights reserved.</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default MagicLinkConfirmation;